import { css } from 'styled-components';
import { checkPixel } from 'utils';
import { theme } from './theme';

const apply = (prop, value) => value && `${prop}: ${value};`;

export const common = css`
  ${({ $display }) => $display && apply('display', $display)};
  ${({ $overflow }) => $overflow && apply('overflow', $overflow)};
  ${({ $overflowY }) => $overflowY && apply('overflow-y', $overflowY)};
  ${({ $visibility }) => $visibility && apply('visibility', $visibility)};
  ${({ $transform }) => $transform && apply('transform', $transform)};
  ${({ $cursor }) => $cursor && apply('cursor', $cursor)};
  ${({ $transition }) => $transition && apply('transition', $transition)};
  ${({ $shadow, theme }) => $shadow && apply('box-shadow', theme.shadow[$shadow])};
  ${({ $bgColor, theme }) => $bgColor && apply('background-color', theme.colors[$bgColor] || $bgColor)};
`;

export const flex = css`
  ${({ $flex }) => $flex && apply('flex', $flex)};
  ${({ $flexWrap }) => $flexWrap && apply('flex-wrap', $flexWrap)};
  ${({ $flexDirection }) => $flexDirection && apply('flex-direction', $flexDirection)};
  ${({ $justify }) => $justify && apply('justify-content', $justify)};
  ${({ $align }) => $align && apply('align-items', $align)};
  ${({ $order }) => $order && apply('order', $order)};
  ${({ $gap }) => $gap && apply('gap', checkPixel($gap))};
  ${({ $rowGap }) => $rowGap && apply('row-gap', checkPixel($rowGap))};
  ${({ $colGap }) => $colGap && apply('column-gap', checkPixel($colGap))};

  @media ${({ theme }) => theme.device.small} {
    ${({ $sFlexDirection }) => $sFlexDirection && apply('flex-direction', $sFlexDirection)};
    ${({ $sAlign }) => $sAlign && apply('align-items', $sAlign)};
  }
`;

export const space = css`
  ${({ $padding }) => $padding && apply('padding', $padding)};
  ${({ $pt }) => $pt && apply('padding-top', checkPixel($pt))};
  ${({ $pr }) => $pr && apply('padding-right', checkPixel($pr))};
  ${({ $pb }) => $pb && apply('padding-bottom', checkPixel($pb))};
  ${({ $pl }) => $pl && apply('padding-left', checkPixel($pl))};

  ${({ $margin }) => $margin && apply('margin', $margin)};
  ${({ $mt }) => $mt && apply('margin-top', checkPixel($mt))};
  ${({ $mr }) => $mr && apply('margin-right', checkPixel($mr))};
  ${({ $mb }) => $mb && apply('margin-bottom', checkPixel($mb))};
  ${({ $ml }) => $ml && apply('margin-left', checkPixel($ml))};

  ${({ $position }) => $position && apply('position', $position)};
  ${({ $top }) => $top && apply('top', checkPixel($top))};
  ${({ $right }) => $right && apply('right', checkPixel($right))};
  ${({ $bottom }) => $bottom && apply('bottom', checkPixel($bottom))};
  ${({ $left }) => $left && apply('left', checkPixel($left))};
  ${({ $zIndex }) => $zIndex && `z-index: ${$zIndex}`};

  ${({ $width }) => $width && apply('width', checkPixel($width))};
  ${({ $maxWidth }) => $maxWidth && apply('max-width', checkPixel($maxWidth))};
  ${({ $minWidth }) => $minWidth && apply('min-width', checkPixel($minWidth))};

  ${({ $height }) => $height && apply('height', checkPixel($height))};
  ${({ $maxHeight }) => $maxHeight && apply('max-height', checkPixel($maxHeight))};
  ${({ $minHeight }) => $minHeight && apply('min-height', checkPixel($minHeight))};

  @media ${({ theme }) => theme.device.small} {
    ${({ $sPadding }) => $sPadding && apply('padding', $sPadding)};
    ${({ $sWidth }) => $sWidth && apply('width', checkPixel($sWidth))};
  }
`;

// typo
const line = css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
`;

const getType = ($type) => {
  const fontType = {
    r: 400,
    m: 500,
    b: 700,
  };

  const type = $type.startsWith('btn') ? $type.slice(3) : $type;
  const match = type.match(/^([a-z]*)(\d+)?$/i);
  const weight = match ? match[1] || 'r' : 'r';
  const fontSize = match && match[2] ? match[2] : '16';
  const fontWeight = fontType[weight] || 400;
  const size = Number(fontSize);
  const lineHeight = $type.startsWith('btn') ? 1 : $type.startsWith('label') ? 1.2 : size >= 38 ? 1.2 : 1.5;

  return `font-weight: ${fontWeight}; font-size: ${size}px; line-height: ${lineHeight};`;
};

export const typography = css`
  ${({ $type }) => getType($type || 'r')}
  ${({ $color }) => $color && apply('color', theme.colors[$color] || $color)};
  ${({ $wordBreak }) => $wordBreak && apply('word-break', $wordBreak)};
  ${({ $whiteSpace }) => $whiteSpace && apply('white-space', $whiteSpace)};
  ${({ $textAlign }) => $textAlign && apply('text-align', $textAlign)};
  ${({ $maxLine }) => $maxLine && `${line} -webkit-line-clamp: ${$maxLine}`};
  ${({ $weight }) => $weight && apply(`font-weight`, $weight)};

  @media ${({ theme }) => theme.device.small} {
    ${({ $sType }) => $sType && getType($sType || 'r')}
  }
`;

// border
const borderStyle = ({ width = 1, style = 'solid', color = 'gray200', opacity = 1 }, theme) =>
  `${checkPixel(width)} ${style} ${theme.colors[color]}`;

const borderApply = (props, theme, propertyName = 'border') => css`
  ${propertyName}: ${borderStyle(props, theme)};
`;

export const border = css`
  ${({ $borderRadius }) => $borderRadius && `border-radius: ${checkPixel($borderRadius)}`};
  ${({ $border, theme }) => $border && borderApply($border, theme)};
  ${({ $borderTop, theme }) => $borderTop && borderApply($borderTop, theme, 'border-top')};
  ${({ $borderRight, theme }) => $borderRight && borderApply($borderRight, theme, 'border-right')};
  ${({ $borderBottom, theme }) => $borderBottom && borderApply($borderBottom, theme, 'border-bottom')};
  ${({ $borderLeft, theme }) => $borderLeft && borderApply($borderLeft, theme, 'border-left')};
`;
